import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CircleCheckBig,
  CircleOff,
  Edit2,
  Network,
  Plus,
  Power,
  Save,
  X,
} from "lucide-react";
import { EmptyState, PageHeader, SectionCard, StatCard } from "@/components/doxa/AdminSurface";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const inputClass = "w-full min-h-11 rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#AAA198] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15 disabled:cursor-not-allowed disabled:bg-[#F7F3EC] disabled:text-[#9B9388]";

export default function Cells() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = () => api.get("/cells", { params: { include_inactive: true } }).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const active = items.filter((item) => item.status === "ACTIVE").length;
    return { total: items.length, active, inactive: items.length - active };
  }, [items]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      await api.post("/cells", { name });
      toast.success("Célula criada");
      setName("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const save = async (id) => {
    if (!editName.trim() || savingId) return;
    setSavingId(id);
    try {
      await api.put(`/cells/${id}`, { name: editName });
      toast.success("Atualizado");
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  const toggle = (cell) => {
    if (togglingId) return;
    setConfirmTarget(cell);
  };

  const confirmToggle = async () => {
    if (!confirmTarget || togglingId) return;
    const cell = confirmTarget;
    const wasActive = cell.status === "ACTIVE";
    setTogglingId(cell.id);
    try {
      await api.post(`/cells/${cell.id}/toggle`);
      await load();
      toast.success(wasActive ? "Célula inativada" : "Célula reativada");
      setConfirmTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTogglingId(null);
    }
  };

  const startEditing = (cell) => {
    setEditingId(cell.id);
    setEditName(cell.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName("");
  };

  const confirmIsActive = confirmTarget?.status === "ACTIVE";

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6 lg:p-8" data-testid="cells-page">
      <PageHeader
        eyebrow="Gestão"
        title="Células"
        description="Organize as células usadas no cadastro e no acompanhamento dos alunos. Inativar preserva o registro e o histórico."
        meta={`${counts.active} ativa${counts.active === 1 ? "" : "s"} · ${counts.inactive} inativa${counts.inactive === 1 ? "" : "s"}`}
      />

      <section className="grid gap-3 sm:grid-cols-3" aria-label="Resumo de células">
        <StatCard
          title="Total"
          value={counts.total}
          hint="Células cadastradas"
          icon={Network}
          tone="orange"
          compact
        />
        <StatCard
          title="Ativas"
          value={counts.active}
          hint="Disponíveis para operação"
          icon={CircleCheckBig}
          tone="success"
          compact
        />
        <StatCard
          title="Inativas"
          value={counts.inactive}
          hint="Registros preservados"
          icon={CircleOff}
          tone="neutral"
          compact
        />
      </section>

      <SectionCard
        title="Nova célula"
        description="Cadastre uma célula pelo nome. O sistema mantém a unicidade e as regras atuais no backend."
        icon={Plus}
        compact
      >
        <form onSubmit={create} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-sm font-medium text-[#3F3A35]">
            Nome da célula
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Célula Central"
              className={`${inputClass} mt-1.5`}
              data-testid="cell-name-input"
              disabled={creating}
            />
          </label>
          <button
            type="submit"
            className="doxa-btn-primary inline-flex min-h-11 items-center justify-center gap-2 px-4 py-2.5 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="cell-create-btn"
            disabled={!name.trim() || creating}
          >
            <Plus className="h-4 w-4" />
            {creating ? "Criando…" : "Criar célula"}
          </button>
        </form>
      </SectionCard>

      <SectionCard
        title="Células cadastradas"
        description="Edite o nome ou altere o status sem apagar registros históricos."
        icon={Network}
        compact
      >
        {items.length === 0 ? (
          <EmptyState
            icon={Network}
            title="Nenhuma célula cadastrada"
            description="Use o formulário acima para criar a primeira célula."
            compact
          />
        ) : (
          <div className="space-y-3">
            {items.map((cell) => {
              const isActive = cell.status === "ACTIVE";
              const isEditing = editingId === cell.id;
              const isSaving = savingId === cell.id;
              const isToggling = togglingId === cell.id;

              return (
                <article
                  key={cell.id}
                  className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-[#FFFEFC] transition hover:border-[#D5CCBE]"
                >
                  <span className={`absolute inset-y-0 left-0 w-1 ${isActive ? "bg-[#49A66D]" : "bg-[#B8AFA4]"}`} aria-hidden="true" />
                  <div className="flex flex-col gap-4 p-4 pl-5 sm:flex-row sm:items-center sm:justify-between sm:p-5 sm:pl-6">
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-[#8A8378]">
                          Editar nome
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className={`${inputClass} mt-1.5`}
                            disabled={isSaving}
                            autoFocus
                          />
                        </label>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2.5">
                          <h3 className="min-w-0 truncate font-display text-lg font-bold text-[#1F1F1F]">{cell.name}</h3>
                          <span className={`doxa-badge ${isActive ? "doxa-badge-success" : "doxa-badge-neutral"}`}>
                            {isActive ? "Ativa" : "Inativa"}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => save(cell.id)}
                            className="doxa-btn-primary inline-flex min-h-10 items-center justify-center gap-1.5 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                            disabled={!editName.trim() || isSaving}
                          >
                            <Save className="h-4 w-4" />
                            {isSaving ? "Salvando…" : "Salvar"}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditing}
                            className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-[#E0D9CD] bg-white px-3 py-2 text-sm font-semibold text-[#4A4640] transition hover:border-[#CFC5B7] hover:bg-[#FAF7F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
                            disabled={isSaving}
                          >
                            <X className="h-4 w-4" /> Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => startEditing(cell)}
                            className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-[#E0D9CD] bg-white px-3 py-2 text-sm font-semibold text-[#4A4640] transition hover:border-[#F75A1B]/30 hover:bg-[#FFF9F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
                          >
                            <Edit2 className="h-4 w-4" /> Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => toggle(cell)}
                            className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 disabled:cursor-not-allowed disabled:opacity-60 ${isActive ? "border-[#E0D9CD] bg-[#F7F3EC] text-[#6B655E] hover:bg-[#F1ECE3]" : "border-[#BFDCC8] bg-[#F2FAF5] text-[#2F855A] hover:bg-[#EAF6EE]"}`}
                            disabled={isToggling || Boolean(togglingId)}
                          >
                            <Power className="h-4 w-4" />
                            {isToggling ? "Atualizando…" : isActive ? "Inativar" : "Reativar"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>

      <AlertDialog
        open={Boolean(confirmTarget)}
        onOpenChange={(open) => {
          if (!open && !togglingId) setConfirmTarget(null);
        }}
      >
        <AlertDialogContent
          className="w-[calc(100%_-_2rem)] max-w-md overflow-hidden rounded-[24px] border border-[#E0D9CD] bg-white p-0 shadow-[0_24px_70px_rgba(40,28,18,0.22)]"
          data-testid="cell-status-confirm-dialog"
        >
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3.5">
              <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${confirmIsActive ? "bg-[#FFF0E9] text-[#F75A1B]" : "bg-[#EAF6EE] text-[#2F855A]"}`}>
                {confirmIsActive ? <CircleOff className="h-5 w-5" /> : <CircleCheckBig className="h-5 w-5" />}
              </span>
              <AlertDialogHeader className="min-w-0 flex-1 space-y-1.5 text-left">
                <AlertDialogTitle className="font-display text-xl font-bold text-[#1F1F1F]">
                  {confirmIsActive ? "Inativar célula?" : "Reativar célula?"}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm leading-relaxed text-[#6B655E]">
                  {confirmIsActive ? (
                    <>
                      A célula <span className="font-semibold text-[#3F3A35]">“{confirmTarget?.name}”</span> deixará de ficar disponível para novas operações.
                    </>
                  ) : (
                    <>
                      A célula <span className="font-semibold text-[#3F3A35]">“{confirmTarget?.name}”</span> voltará a ficar disponível para uso no sistema.
                    </>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
            </div>

            <div className="mt-4 rounded-2xl border border-[#EBE5DB] bg-[#FAF7F1] px-4 py-3 text-xs leading-relaxed text-[#6B655E]">
              Nenhum histórico será excluído. O registro e os vínculos existentes serão preservados.
            </div>

            <AlertDialogFooter className="mt-6 gap-2 sm:space-x-0">
              <AlertDialogCancel
                disabled={Boolean(togglingId)}
                className="mt-0 min-h-10 rounded-xl border-[#E0D9CD] bg-white px-4 font-semibold text-[#4A4640] hover:bg-[#FAF7F2] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </AlertDialogCancel>
              <button
                type="button"
                onClick={() => void confirmToggle()}
                disabled={Boolean(togglingId)}
                className="inline-flex min-h-10 items-center justify-center rounded-xl bg-[#F75A1B] px-4 text-sm font-semibold text-white transition hover:bg-[#DF4D13] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/35 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="cell-status-confirm-action"
              >
                {togglingId ? "Atualizando…" : confirmIsActive ? "Inativar célula" : "Reativar célula"}
              </button>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
